import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { DEFAULT_ROTATE_PROMPT_MAX_WIDTH, shouldShowRotatePromptFromMetrics } from "@/utils/phoneDevice";
import Adjectives from "./Adjectives";
import { isValidElement, useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import Behaviors from "./Behaviors";
import BehaviorsStatic from "./BehaviorsStatic";
import CMotivators from "./CMotivators";
import Colorsheet from "./Colorsheet";
import ColorsheetVertical from "./ColorsheetVertical";
import Cultures from "./Cultures";
import Descriptors from "./Descriptors";
import Done from "./Done";
import Welcome from "./Welcome";
import WordPairs from "./WordPairs";
import WordSquares from "./WordSquares";
import WordSquaresLegacy from "./WordSquaresLegacy";
import NotFound from "@errors/NotFound";
import TimedOut from "@errors/TimedOut";
import AlreadyTaken from "@errors/AlreadyTaken";
import PositionInventory from "./PositionInventory";
import BehavioralInventory from "./BehavioralInventory";
import PageHeader from "@/elements/PageHeader";
import ProctorProvidedFieldsBar from "@/elements/ProctorProvidedFieldsBar";
import SectionControls from "@/elements/SectionControls";
import Helpers from "@/Helpers";
import Rose30 from "./Rose30";
import Motivators60 from "./Motivators60";
import Motivators6 from "./Motivators6";
import Anxieties from "./Anxieties";
import Beauties from "./Beauties";
import Checklist from "./Checklist";
import UserPrompt from "./UserPrompt";
import type { SectionControlElements, SectionRendererProps, SectionCurrentSection, SectionRuntimeRenderData } from "./sectionRuntimeTypes";

let persistedAdminMode = false;

type SectionComponentProps = {
    sectionData: SectionRuntimeRenderData;
    values: Record<string, string>;
    setValue: (fieldId: string, value: string) => Promise<void> | void;
};

const SECTION_COMPONENTS: Record<string, ComponentType<SectionComponentProps>> = {
    Anxieties,
    Adjectives,
    Beauties,
    Behaviors,
    BehaviorStatic: BehaviorsStatic,
    BehaviorsStatic,
    CMotivators,
    Motivators60,
    Motivators6,
    ColorSheet: Colorsheet,
    ColorSheetVertical: ColorsheetVertical,
    CulturesActual: Cultures,
    CulturesCurrent: Cultures,
    CulturesIdeal: Cultures,
    Descriptors,
    PositionInventory,
    BehavioralInventory,
    Done,
    Welcome,
    WordPairs,
    WordSquares,
    WordSquaresLegacy,
    Rose30,
    Checklist,
    UserPrompt
};

const ERROR_COMPONENTS: Record<string, ComponentType> = {
    "404": NotFound,
    Error_404: NotFound,
    AlreadyTaken,
    TimedOut,
};

export function canRenderSection(sectionName: string | null | undefined): boolean {
    const name = String(sectionName || "");
    return Boolean(name && (SECTION_COMPONENTS[name] || ERROR_COMPONENTS[name]));
}

const REACTIVE_ISSUE_COUNT_SECTIONS = new Set([
    "Adjectives",
    "Anxieties",
    "Beauties",
    "BehavioralInventory",
    "CMotivators",
    "ColorSheet",
    "ColorSheetVertical",
    "CulturesActual",
    "CulturesCurrent",
    "CulturesIdeal",
    "Motivators6",
    "Motivators60",
    "Rose30",
    "WordSquares",
    "WordSquaresLegacy",
    "Checklist",
    "UserPrompt",
]);

const FORCE_DISABLE_WHEN_PENDING_SECTIONS = new Set([
    "Adjectives",
    "Checklist",
    "CMotivators",
    "ColorSheet",
    "ColorSheetVertical",
    "CulturesActual",
    "WordSquares",
    "UserPrompt",
]);

function areRecordValuesEqual(a: Record<string, string>, b: Record<string, string>) {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
        if (a[key] !== b[key]) return false;
    }

    return true;
}

function getAdvanceValidationState(nextCount: number, issueCount: number) {
    const nextValidationAttempt = Number(nextCount || 0) + 1;
    const hasIssues = Number(issueCount || 0) > 0;
    return { nextValidationAttempt, hasIssues };
}

function getRotatePromptMaxWidth(currentSection, runtimeOverride = null) {
    if (Number.isFinite(runtimeOverride)) {
        return runtimeOverride;
    }

    const sectionValue = currentSection?.rotatePromptMaxWidth;
    return Number.isFinite(sectionValue) ? sectionValue : DEFAULT_ROTATE_PROMPT_MAX_WIDTH;
}

function shouldShowRotatePrompt(currentSection, runtimeOverride = null) {
    if (typeof window === "undefined") return false;

    const viewportWidth = Math.round(window.visualViewport?.width || window.innerWidth || 0);
    const viewportHeight = Math.round(window.visualViewport?.height || window.innerHeight || 0);

    return shouldShowRotatePromptFromMetrics({
        viewportWidth,
        viewportHeight,
        screenWidth: window.screen?.width || 0,
        screenHeight: window.screen?.height || 0,
        rotatePromptMaxWidth: getRotatePromptMaxWidth(currentSection, runtimeOverride),
    });
}

function getRenderableRotateMessage(message: unknown): ReactNode | null {
    if (message == null || typeof message === "boolean") return null;
    if (typeof message === "string") return parseSanitizedHtml(message);
    if (typeof message === "number") return message;
    if (isValidElement(message)) return message;
    if (Array.isArray(message)) {
        return message.every((item) =>
            item == null ||
            typeof item === "boolean" ||
            typeof item === "string" ||
            typeof item === "number" ||
            isValidElement(item)
        )
            ? (message as ReactNode[])
            : null;
    }

    return null;
}

export default function SectionRenderer(props: SectionRendererProps) {
    const { sectionData, hooks = {}, fixedPage, initialPage, initialValues } = props;
    const isReplayMode = Boolean(sectionData?.testState?.replayMode);
    const hidePageHeader = Boolean(sectionData?.testState?.showData);
    const hideControls = isReplayMode || Boolean(sectionData?.testState?.showData);
    const [values, setValues] = useState({});
    const [showValidations, setShowValidations] = useState(false);
    const [buttonDisablable, setButtonDisablable] = useState(false);
    const [nextCount, setNextCount] = useState(0);
    const [reportedIssueCount, setReportedIssueCount] = useState<{ attempt: number; count: number } | null>(null);
    const [forceNextAsSection, setForceNextAsSection] = useState(false);
    const [isAdminMode, setIsAdminMode] = useState(() => sectionData?.isAdmin === true || persistedAdminMode);
    const valuesRef = useRef({});
    const sectionContainerRef = useRef<HTMLDivElement | null>(null);
    const valueTimesRef = useRef<Record<string, string>>({});
    const defaultValuesRef = useRef<Record<string, string>>({});
    const [rotatePromptMaxWidthOverride, setRotatePromptMaxWidthOverride] = useState<number | null>(null);
    const [showRotatePrompt, setShowRotatePrompt] = useState(() =>
        isReplayMode ? false : shouldShowRotatePrompt(sectionData?.currentSection, null));
    const [page, setPage] = useState(fixedPage ?? initialPage ?? 1);
    const [pendingAdvance, setPendingAdvance] = useState<{
        mode: "page" | "section";
        bypassValidation: boolean;
        attempt: number;
    } | null>(null);

    const effectivePage = fixedPage ?? page;

    const restoredValuesRef = useRef<Record<string, string> | undefined>(initialValues);
    restoredValuesRef.current = initialValues;

    useEffect(() => {
        if (!initialValues) return;
        setValues((prev) => {
            const next = { ...(prev as Record<string, string>), ...initialValues };
            valuesRef.current = next;
            return next;
        });
    }, [initialValues]);

    useEffect(() => {
        if (fixedPage == null) return;
        setPage(fixedPage);
    }, [fixedPage]);
    // SectionRenderer only reads from it; it never writes back.
    const currentSection: SectionCurrentSection = sectionData?.currentSection ?? { formId: 0 };
    const sectionControlsByAbbrev = (sectionData?.sectionControlsByAbbrev
        ?? sectionData?.testState?.sectionControlsByAbbrev) as Record<string, SectionControlElements> | undefined;
    const sectionControls = (sectionData?.sectionControls
        ?? sectionData?.testState?.sectionControls) as SectionControlElements | undefined;
    const controlElements = Helpers.resolveSectionControlElements(
        sectionControls,
        sectionControlsByAbbrev,
        currentSection,
        sectionData?.lang || "eng",
    );
    const usesReactiveIssueCount = REACTIVE_ISSUE_COUNT_SECTIONS.has(currentSection?.name || "");
    const forceDisableOnPending = FORCE_DISABLE_WHEN_PENDING_SECTIONS.has(currentSection?.name || "");

    useEffect(() => {
        valuesRef.current = values || {};
    }, [values]);

    const handleAdminToggleKeyDown = useCallback((event) => {
        if (!event) return;
        // When called from a React synthetic event handler the native DOM event is
        // event.nativeEvent; when called from a plain document listener it IS the
        // native event.  Always stamp the native event so both call-sites share the
        // same flag and we never fire the toggle twice for one physical keypress.
        const nativeEv: { __adminToggleHandled?: boolean; repeat?: boolean } =
            (event as { nativeEvent?: { __adminToggleHandled?: boolean; repeat?: boolean } }).nativeEvent ?? event;
        if (nativeEv.__adminToggleHandled) return;
        if (nativeEv.repeat) return;
        if (!event?.altKey || !event?.shiftKey || !event?.ctrlKey) return;

        const keyValue = typeof event.key === "string" ? event.key.toLowerCase() : "";
        const codeValue = typeof event.code === "string" ? event.code : "";
        const isToggleKey = keyValue === "x" || codeValue === "KeyX";
        if (!isToggleKey) return;

        nativeEv.__adminToggleHandled = true;
        if (typeof event.preventDefault === "function") {
            event.preventDefault();
        }
        setIsAdminMode((prev) => {
            const next = !prev;
            persistedAdminMode = next;
            return next;
        });
    }, []);

    useEffect(() => {
        if (sectionData?.isAdmin === true && !isAdminMode) {
            setIsAdminMode(true);
            persistedAdminMode = true;
        }
    }, [sectionData?.isAdmin, isAdminMode]);

    useEffect(() => {
        const documentKeydownHandler = (event) => {
            handleAdminToggleKeyDown(event);
        };

        document.addEventListener("keydown", documentKeydownHandler);
        return () => {
            document.removeEventListener("keydown", documentKeydownHandler);
        };
    }, [handleAdminToggleKeyDown]);

    const buildQuestionValueKey = useCallback((fieldId) => {
        const fieldIdStr = String(fieldId);
        const match = fieldIdStr.match(/^(\d+)([a-zA-Z]*)$/);
        let numericPart = fieldIdStr;
        let alphaPart = "";
        if (match) {
            numericPart = match[1];
            alphaPart = match[2];
        }

        const formId = Number(currentSection?.formId || 0);
        const numericValue = Number(numericPart);

        if (!alphaPart) {
            return fieldId > 100
                ? fieldId.toString()
                : (formId + numericValue).toString();
        }

        return (formId + numericValue).toString() + alphaPart;
    }, [currentSection?.formId]);

    function focusFirstIssueInput() {
        setTimeout(() => {
            const firstErrorInput = document.querySelector("input.oops") as HTMLInputElement | null;
            if (firstErrorInput && typeof firstErrorInput.focus === "function") {
                firstErrorInput.focus();
            }
            if (firstErrorInput && typeof firstErrorInput.select === "function") {
                firstErrorInput.select();
            }
        }, 100);
    }

    const registerSection = useCallback((
        rotatePromptMaxWidth = null,
        answerCount = 0,
        disableButtonToggle = true,
    ) => {
        const formId = Number(currentSection?.formId || 0);
        const nextValues: Record<string, string> = {};
        const defaults: Record<string, string> = {};

        if (!isReplayMode && answerCount > 0 && formId > 0) {
            for (let i = 1; i <= answerCount; i++) {
                const key = String(formId + i);
                nextValues[key] = "0";
                defaults[key] = "0";
            }
        }

        const restored = restoredValuesRef.current;
        if (restored) {
            for (const [key, value] of Object.entries(restored)) {
                if (value == null || value === "") continue;
                nextValues[key] = String(value);
            }
        }

        setValues((prev) => {
            if (areRecordValuesEqual(prev as Record<string, string>, nextValues)) {
                return prev;
            }
            return nextValues;
        });
        valuesRef.current = nextValues;
        valueTimesRef.current = {};
        defaultValuesRef.current = defaults;

        setShowValidations((prev) => (prev ? false : prev));
        setButtonDisablable((prev) => (prev === disableButtonToggle ? prev : disableButtonToggle));
        setNextCount((prev) => (prev === 0 ? prev : 0));
        setReportedIssueCount((prev) => (prev === null ? prev : null));
        setForceNextAsSection((prev) => (prev ? false : prev));
        setPendingAdvance((prev) => (prev === null ? prev : null));

        if (Number.isFinite(rotatePromptMaxWidth)) {
            setRotatePromptMaxWidthOverride((prev) => {
                return Number(prev) === rotatePromptMaxWidth ? prev : rotatePromptMaxWidth;
            });
        }
    }, [currentSection?.formId, isReplayMode]);

    const handleAnswer = useCallback(async (fieldId, value) => {
        const questionValueKey = buildQuestionValueKey(fieldId);
        const baseValues = valuesRef.current || {};
        const defaultValues = defaultValuesRef.current || {};
        let nextValues = baseValues;

        if (value === null || value === undefined || value === "") {
            nextValues = { ...baseValues };
            if (Object.prototype.hasOwnProperty.call(defaultValues, questionValueKey)) {
                nextValues[questionValueKey] = defaultValues[questionValueKey];
            } else {
                delete nextValues[questionValueKey];
            }
        } else {
            nextValues = { ...baseValues, [questionValueKey]: value };
        }

        setValues(nextValues);
        valuesRef.current = nextValues;
        valueTimesRef.current = {
            ...(valueTimesRef.current || {}),
            [questionValueKey]: new Date().toISOString(),
        };

        if (typeof hooks?.onAnswer === "function") {
            await hooks.onAnswer({
                sectionId: Number(currentSection?.id || 0),
                formId: Number(currentSection?.formId || 0),
                questionId: String(fieldId),
                response: value,
                page: page,
                pages: Number(currentSection?.pages || 0),
            });
        }
    }, [buildQuestionValueKey, currentSection?.formId, currentSection?.id, currentSection?.pages, page, hooks]);

    const getIssueCount = useCallback((attempt = Number(nextCount || 0)) => {
        if (
            usesReactiveIssueCount
            && reportedIssueCount !== null
            && Number(reportedIssueCount.attempt) === Number(attempt)
        ) {
            return Number(reportedIssueCount.count || 0);
        }
        return 0;
    }, [nextCount, usesReactiveIssueCount, reportedIssueCount]);

    useEffect(() => {
        const shouldLiveValidate = nextCount > 0 || showValidations;
        if (!shouldLiveValidate) {
            return;
        }

        // Update showValidations based on current validation state
        if (showValidations && getIssueCount(nextCount) === 0) {
            // Clear validations if there are no more issues
            setShowValidations(false);
        }
    }, [page, nextCount, reportedIssueCount, showValidations, values, getIssueCount]);

    useEffect(() => {
        if (!pendingAdvance) {
            return;
        }

        if (pendingAdvance.attempt !== Number(nextCount || 0)) {
            return;
        }

        if (
            usesReactiveIssueCount
            && (
                reportedIssueCount === null
                || Number(reportedIssueCount.attempt) !== Number(pendingAdvance.attempt)
            )
        ) {
            return;
        }

        const completeAdvanceAsync = async () => {
            const currentValues = valuesRef.current || {};
            const issueCount = Number(getIssueCount(pendingAdvance.attempt) || 0);
            const hasIssues = issueCount > 0;

            if (hasIssues && !pendingAdvance.bypassValidation) {
                setNextCount(pendingAdvance.attempt);
                setShowValidations(true);
                if (typeof hooks?.onNextAttempt === "function") {
                    await hooks.onNextAttempt({
                        sectionId: Number(currentSection?.id || 0),
                        formId: Number(currentSection?.formId || 0),
                        questionId: "NextPage",
                        response: String(issueCount),
                        page: Number(page || 0),
                        pages: Number(currentSection?.pages || 0),
                    });
                }
                focusFirstIssueInput();
                setPendingAdvance(null);
                return;
            }

            if (pendingAdvance.mode === "page") {
                const toPage = page + 1;
                const canAdvance = Number(currentSection?.pages || 0) > 0 && page < Number(currentSection?.pages || 0);
                if (canAdvance) {
                    setShowValidations(false);
                    setNextCount(0);
                    setPage(toPage);
                    setPendingAdvance(null);
                    if (typeof hooks?.onPageChanged === "function") {
                        void hooks.onPageChanged({
                            sectionId: Number(currentSection?.id || 0),
                            formId: Number(currentSection?.formId || 0),
                            questionId: "NextPage",
                            response: "0",
                            page: toPage,
                            pages: Number(currentSection?.pages || 0),
                        });
                    }
                    return;
                }

                // Admin keyboard Enter should only ever advance page, never section.
                if (isAdminMode) {
                    setPendingAdvance(null);
                    return;
                }
            }

            setShowValidations(false);
            setNextCount(0);

            if (typeof hooks?.onSectionCompleted === "function") {
                await hooks.onSectionCompleted({
                    sectionId: Number(currentSection?.id || 0),
                    formId: Number(currentSection?.formId || 0),
                    questionId: "NextSection",
                    response: "0",
                    page: Number(page || 0),
                    pages: Number(currentSection?.pages || 0),
                    values: currentValues,
                    valueTimes: valueTimesRef.current || {},
                });
            }

            setPendingAdvance(null);
        };

        void completeAdvanceAsync();
    }, [currentSection?.formId, currentSection?.id, currentSection?.pages, hooks, nextCount, page, pendingAdvance, reportedIssueCount, usesReactiveIssueCount, getIssueCount, isAdminMode]);

    const handleAdvance = useCallback(async (
        mode: "page" | "section",
        e?: unknown,
        bypassValidation = false,
    ) => {
        if (e && typeof (e as { preventDefault?: () => void }).preventDefault === "function") {
            (e as { preventDefault: () => void }).preventDefault();
        }

        const shouldForceAsSection = forceNextAsSection;
        if (shouldForceAsSection) {
            setForceNextAsSection(false);
        }

        const effectiveMode: "page" | "section" = shouldForceAsSection ? "section" : mode;
        const effectiveBypassValidation = shouldForceAsSection ? true : bypassValidation;
        const { nextValidationAttempt } = getAdvanceValidationState(nextCount, 0);

        setNextCount(nextValidationAttempt);
        setPendingAdvance({ mode: effectiveMode, bypassValidation: effectiveBypassValidation, attempt: nextValidationAttempt });
    }, [nextCount, forceNextAsSection]);

    const handleNextPage = useCallback(async (e) => {
        await handleAdvance("page", e, false);
    }, [handleAdvance]);

    const handleNextSection = useCallback(async (e, bypassValidation = false) => {
        await handleAdvance("section", e, bypassValidation);
    }, [handleAdvance]);

    const sectionDataForRender: SectionRuntimeRenderData = {
        currentSection,
        lang: sectionData?.lang,
        testState: sectionData?.testState,
        sectionControls,
        sectionControlsByAbbrev,
        controlElements,
        showValidations,
        setShowValidations,
        page: Number(effectivePage || 0),
        nextCount,
        updateIssueCount: (count) => {
            const attempt = Number((pendingAdvance?.attempt ?? nextCount) || 0);
            const next = { attempt, count: Number(count || 0) };
            setReportedIssueCount((prev) => {
                if (!prev) return next;
                if (Number(prev.attempt) === next.attempt && Number(prev.count) === next.count) {
                    return prev;
                }
                return next;
            });
        },
        requestNextAsSection: () => {
            setForceNextAsSection(true);
        },
        buttonDisablable,
        onKeyDown: handleAdminToggleKeyDown,
        isAdmin: isAdminMode,
        registerSection,
        onAnswer: handleAnswer,
        onNextPage: handleNextPage,
        onNextSection: handleNextSection,
    };

    useEffect(() => {
        if (isReplayMode) {
            setShowRotatePrompt(false);
            return;
        }

        const updateRotatePrompt = () => {
            setShowRotatePrompt(shouldShowRotatePrompt({ rotatePromptMaxWidth: currentSection?.rotatePromptMaxWidth }, rotatePromptMaxWidthOverride));
        };

        updateRotatePrompt();
        window.addEventListener("resize", updateRotatePrompt);
        window.addEventListener("orientationchange", updateRotatePrompt);
        window.visualViewport?.addEventListener("resize", updateRotatePrompt);

        return () => {
            window.removeEventListener("resize", updateRotatePrompt);
            window.removeEventListener("orientationchange", updateRotatePrompt);
            window.visualViewport?.removeEventListener("resize", updateRotatePrompt);
        };
    }, [currentSection?.name, currentSection?.rotatePromptMaxWidth, rotatePromptMaxWidthOverride, isReplayMode]);

    useEffect(() => {
        if (!showRotatePrompt && sectionContainerRef.current) {
            sectionContainerRef.current.scrollTop = 0;
        }
    }, [showRotatePrompt]);

    const setValueForRender = isReplayMode ? () => undefined : handleAnswer;

    let renderedSection = null;
    const sectionName = currentSection?.name || "";
    if (!sectionName) {
        return null;
    }
    const rotateMessage = getRenderableRotateMessage(sectionData?.testState?.rotateMessage);
    const SectionComponent = SECTION_COMPONENTS[sectionName];
    if (SectionComponent) {
        renderedSection = <SectionComponent sectionData={sectionDataForRender} values={values} setValue={setValueForRender} />;
    } else {
        const ErrorComponent = ERROR_COMPONENTS[sectionName];
        if (!ErrorComponent) {
            return null;
        }
        renderedSection = <ErrorComponent />;
    }

    return (
        <div className={`testSectionContainer${isReplayMode ? " testAnswerReplay" : ""}`}>
            {!isReplayMode ? (
                <ProctorProvidedFieldsBar testState={sectionData?.testState} mode="inTest" />
            ) : null}
            {isAdminMode && !isReplayMode && (
                <div className="adminInventory">Admin Mode</div>
            )}
            {showRotatePrompt ? (
                <div className="rotateDevicePrompt">
                    {rotateMessage ?? (
                        <>
                            <h2>Please rotate device</h2>
                            <p>This questionnaire is best viewed in landscape mode.</p>
                        </>
                    )}
                </div>
            ) : null}
            <div ref={sectionContainerRef} className={`section ${currentSection.name} ${sectionData?.lang || "eng"}${showRotatePrompt ? " rotatePromptHidden" : ""}`}>
                {currentSection && !hidePageHeader && (
                    <PageHeader
                        testState={{ ...sectionData?.testState, lang: sectionData?.lang }}
                        currentSection={currentSection}
                        page={effectivePage}
                        controlElements={controlElements}
                    />
                )}
                {renderedSection}
                {!hideControls && (
                    <SectionControls
                        onNextSection={handleNextSection}
                        issueCount={getIssueCount()}
                        showValidations={showValidations}
                        page={sectionDataForRender.page}
                        pages={Number(currentSection?.pages || 0)}
                        lang={sectionData?.lang}
                        onNextPage={handleNextPage}
                        currentSection={currentSection}
                        startTestButtonText={sectionData?.testState?.startTestButtonText}
                        testState={sectionData?.testState}
                        controlElements={controlElements}
                        buttonDisablable={buttonDisablable}
                        forceDisableOnPending={forceDisableOnPending}
                        advanceInProgress={pendingAdvance !== null}
                        isAdmin={isAdminMode}
                    />
                )}
            </div>
        </div>
    );
}
